import {
  Avatar,
  Badge,
  Button,
  Divider,
  makeStyles,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Text,
  tokens,
  Tooltip,
} from '@fluentui/react-components';
import { LineHorizontal3Regular, PersonRegular, SignOutRegular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router';

import { config } from '@/app/config';
import { useAuth } from '@/features/auth/useAuth';
import { paths } from '@/routes/paths';

import { ThemeToggle } from './ThemeToggle';

export const TOP_BAR_HEIGHT = '40px';

const useStyles = makeStyles({
  bar: {
    gridColumn: '1 / -1',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    height: TOP_BAR_HEIGHT,
    paddingInline: tokens.spacingHorizontalS,
    // Whichever primary colour the user has chosen. `palettes.ts` decides which
    // ramp stop this token points at, and holds the contrast policy: every
    // palette keeps white text at AA except the Bedrock orange, which is pinned
    // to its vivid stop 120 (2.15:1) as a documented brand-fidelity exception.
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,

    // Fluent buttons default to neutral foregrounds, which vanish on the brand
    // background. Restyled with one descendant selector rather than by nesting
    // a second FluentProvider, which would duplicate the whole CSS-variable
    // block (see ThemeProvider).
    '& .fui-Button': {
      color: tokens.colorNeutralForegroundOnBrand,
      minWidth: '32px',
    },
    '& .fui-Button:hover': {
      color: tokens.colorNeutralForegroundOnBrand,
      backgroundColor: 'rgba(255, 255, 255, 0.16)',
    },
    '& .fui-Button:active': { backgroundColor: 'rgba(255, 255, 255, 0.24)' },
  },
  mark: {
    height: '26px',
    width: 'auto',
    display: 'block',
    // The mark is dark slate, grey and orange, which disappears on the orange
    // bar. This renders it solid white without needing a second asset.
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  appName: {
    color: tokens.colorNeutralForegroundOnBrand,
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  spacer: { flex: 1 },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  greeting: {
    color: tokens.colorNeutralForegroundOnBrand,
    whiteSpace: 'nowrap',
    marginInlineStart: tokens.spacingHorizontalXS,
  },
  identity: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    maxWidth: '18rem',
  },
  email: { color: tokens.colorNeutralForeground2, overflowWrap: 'anywhere' },
  badgeRow: { marginTop: tokens.spacingVerticalXS },
  avatarButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
});

interface TopBarProps {
  onToggleNav: () => void;
  navExpanded: boolean;
}

export function TopBar({ onToggleNav, navExpanded }: TopBarProps) {
  const styles = useStyles();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    void navigate(paths.login, { replace: true });
  };

  return (
    <header className={styles.bar}>
      <Tooltip
        content={navExpanded ? 'Collapse navigation' : 'Expand navigation'}
        relationship="label"
      >
        <Button
          appearance="transparent"
          size="small"
          icon={<LineHorizontal3Regular />}
          aria-label={navExpanded ? 'Collapse navigation' : 'Expand navigation'}
          aria-expanded={navExpanded}
          onClick={onToggleNav}
        />
      </Tooltip>

      <div className={styles.brand}>
        <img className={styles.mark} src="/brand/bedrock-mark.png" alt="" />
        <Text className={styles.appName} size={300}>
          {config.appName}
        </Text>
      </div>

      <div className={styles.spacer} />

      <div className={styles.actions}>
        <ThemeToggle />

        {user && (
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <button
                type="button"
                className={styles.avatarButton}
                aria-label={`Account menu for ${user.name}`}
              >
                <Avatar name={user.name} size={28} color="colorful" />
                <Text className={styles.greeting} size={200}>
                  {user.name}
                </Text>
              </button>
            </MenuTrigger>

            <MenuPopover>
              <div className={styles.identity}>
                <Text weight="semibold">{user.name}</Text>
                <Text size={200} className={styles.email}>
                  {user.email}
                </Text>
                <div className={styles.badgeRow}>
                  {/* A label, not a permission: clients and contacts have
                      identical capabilities. Shown so people can tell which of
                      their two accounts they are signed in to. */}
                  <Badge appearance="tint" color="informative">
                    {user.user_type === 'client' ? 'Client' : 'Contact'}
                  </Badge>
                </div>
              </div>

              <Divider />

              <MenuList>
                <MenuItem icon={<PersonRegular />} onClick={() => void navigate(paths.profile)}>
                  Your details
                </MenuItem>
                <MenuDivider />
                <MenuItem icon={<SignOutRegular />} onClick={() => void handleSignOut()}>
                  Sign out
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        )}
      </div>
    </header>
  );
}
